<template>
  <el-dialog
    :title="fuel.exists ? fuel.name : 'New Fuel Levy'"
    visible
    width="80vw"
    top="5vh"
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    @close="$emit('update:isShow', false)"
  >
    <el-form
      ref="fuelForm"
      v-loading="isSaving"
      :rules="rules"
      :model="fuel"
      size="small"
    >
      <el-row :gutter="30">
        <el-col :md="12">
          <h1>
            General<br>
            <small>
              Description of general updates you're making
            </small>
          </h1>
          <el-row :gutter="10">
            <el-col :md="16">
              <el-form-item
                label="Name"
                prop="name"
              >
                <el-input
                  v-model="fuel.name"
                  placeholder="Name"
                  clearable
                />
              </el-form-item>
            </el-col>
            <el-col :md="8">
              <el-form-item
                label="Type"
                prop="type"
              >
                <el-select
                  v-model="fuel.type"
                  placeholder="Type"
                  class="w-100"
                >
                  <el-option
                    label="Buy"
                    value="buy"
                  />
                  <el-option
                    label="Sell"
                    value="sell"
                  />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row>
            <el-col>
              <el-form-item
                label="Rate"
                prop="rate"
              >
                <el-input-number
                  v-model="fuel.rate"
                  :precision="4"
                  controls-position="right"
                  placeholder="Rate"
                  class="w-100"
                />
              </el-form-item>
            </el-col>
          </el-row>
          <el-row>
            <el-col>
              <el-form-item
                label="Carrier"
                prop="carrier"
              >
                <el-select
                  v-model="fuel.carrier"
                  filterable
                  remote
                  placeholder="Enter name of carrier"
                  class="w-100"
                  value-key="id"
                  :remote-method="remoteMethodCarrier"
                  :loading="isLoadingCarriers"
                  :disabled="!!fuel.id"
                >
                  <el-option
                    v-for="item in carriers"
                    :key="item.id"
                    :label="item.name"
                    :value="item"
                  />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
        </el-col>
        <el-col :md="12">
          <h1>
            Dates<br>
            <small>
              Modify the dates and describe the implications
            </small>
          </h1>
          <el-row>
            <el-col>
              <el-form-item
                label="Start"
                prop="date_start"
              >
                <el-date-picker
                  v-model="fuel.date_start"
                  type="date"
                  format="dd/MM/yyyy"
                  value-format="yyyy-MM-dd"
                  placeholder="Pick a Start Date"
                  :picker-options="datePickerOption"
                  class="w-100"
                  :disabled="!fuel.id"
                />
              </el-form-item>
            </el-col>
          </el-row>
          <el-row>
            <el-col>
              <el-form-item
                label="End"
                prop="date_end"
              >
                <el-date-picker
                  v-model="fuel.date_end"
                  type="date"
                  format="dd/MM/yyyy"
                  value-format="yyyy-MM-dd"
                  placeholder="Pick a End Date"
                  :picker-options="datePickerOption"
                  class="w-100"
                  :disabled="!fuel.id"
                />
              </el-form-item>
            </el-col>
          </el-row>
          <el-divider />
          <el-row>
            <el-col>
              <h1>
                Active<br>
                <small>
                  This is a description of the implications of changing the active field + dates.<br>For example setting to active will automatically attempt to end the previous Fuel Levy (if only one is found).
                </small>
              </h1>
              <el-row>
                <el-col>
                  <el-form-item
                    label="Active"
                    prop="active"
                  >
                    <el-select
                      v-model="fuel.active"
                      placeholder="Choose active or inactive"
                      class="w-100"
                      :disabled="!fuel.id"
                    >
                      <el-option
                        label="Yes"
                        :value="true"
                      />
                      <el-option
                        label="No"
                        :value="false"
                      />
                    </el-select>
                  </el-form-item>
                </el-col>
              </el-row>
            </el-col>
          </el-row>
        </el-col>
      </el-row>
    </el-form>
    <span
      slot="footer"
      class="dialog-footer"
    >
      <el-button @click.native="$emit('update:isShow', false)">
        Cancel
      </el-button>

      <el-button
        type="primary"
        :loading="isSaving"
        @click.native="fuel.exists ? updateFuel() : createFuel()"
      >
        {{ fuel.exists ? 'Save' : 'Create' }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
export default {
  props: {
    fuel: {
      type: Object,
      required: true
    },
    isShow: {
      type: Boolean,
      required: true
    }
  },
  data () {
    const validateDateStart = (rule, value, callback) => {
      if ((value === null || value === undefined) && this.fuel.active === true) {
        return callback(new Error('Start Date is required when fuel levy is active'))
      } else {
        callback()
      }
    }
    return {
      isSaving: false,
      isFullscreen: false,
      isLoadingCarriers: false,
      carriers: [],
      datePickerOption: {
        shortcuts: [{
          text: 'Today',
          onClick (picker) {
            picker.$emit('pick', new Date())
          }
        }, {
          text: 'Yesterday',
          onClick (picker) {
            const date = new Date()
            date.setTime(date.getTime() - 3600 * 1000 * 24)
            picker.$emit('pick', date)
          }
        }, {
          text: 'A week ago',
          onClick (picker) {
            const date = new Date()
            date.setTime(date.getTime() - 3600 * 1000 * 24 * 7)
            picker.$emit('pick', date)
          }
        }]
      },
      rules: {
        name: [
          { required: true, message: 'Name of the Rate Card is required', trigger: ['blur', 'change'] },
          { min: 5, max: 45, message: 'Length should be 5 to 45', trigger: ['blur', 'change'] }
        ],
        type: [
          { required: true, message: 'Type of the Rate Card is required', trigger: ['blur', 'change'] }
        ],
        rate: [
          { required: true, message: 'Fuel Levy Rate is required', trigger: ['blur', 'change'] }
        ],
        carrier: [
          { required: true, message: 'Carrier is required', trigger: 'change' }
        ],
        date_start: [
          { required: true, validator: validateDateStart, trigger: 'change' }
        ]
      }
    }
  },
  created () {
    if (this.fuel.carrier) {
      this.remoteMethodCarrier(this.fuel.carrier.name)
    }
  },
  methods: {
    createFuel () {
      this.$refs.fuelForm.validate((valid) => {
        if (valid) {
          this.isSaving = true
          this.fuel.group = new this.$sdk.fuel_group()
          this.$sdk.fuel.create(this.fuel, builder => {
            builder.include = ['carrier']
          })
            .then(() => this.$message({ message: 'Successfully saved', type: 'success' }))
            .finally(() => {
              this.isSaving = false
              this.$emit('saved')
              this.$emit('update:isShow', false)
            })
        }
      })
    },
    updateFuel () {
      this.$refs.fuelForm.validate((valid) => {
        if (valid) {
          this.isSaving = true

          if (this.fuel.date_start) {
            this.fuel.date_start = this.$moment(this.fuel.date_start).set({ hour: 0, minute: 0, second: 0 }).toDate()
          }

          if (this.fuel.date_end) {
            this.fuel.date_end = this.$moment(this.fuel.date_end).set({ hour: 23, minute: 59, second: 59 }).toDate()
          }

          this.fuel.update(builder => {
            builder.include = ['carrier']
          })
            .then(() => this.$message({ message: 'Successfully saved', type: 'success' }))
            .finally(() => {
              this.isSaving = false
              this.$emit('saved')
              this.$emit('update:isShow', false)
            })
        }
      })
    },
    remoteMethodCarrier (query) {
      this.isLoadingCarriers = true
      this.$sdk.carrier.list(builder => {
        builder.filters = { 'carrier_name': query }
      }).then(res => {
        this.carriers = res
      }).finally(() => { this.isLoadingCarriers = false })
    }
  }
}
</script>

<style>

</style>
